
import { Link } from 'react-router-dom';

export default function ArticlePartTwo() {
  return (
    <div className="prose prose-lg max-w-none">
      <h2
        id="stage-2-active-listing-and-showings-3-to-8-weeks"
        className="text-4xl font-bold text-black mt-16 mb-6"
      >
        Stage 2: Active Listing &amp; Showings (3 to 8 Weeks)
      </h2>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Once your home is prepped, photographed, and priced, your agent will enter it into the El Paso MLS (Multiple Listing Service), which syndicates your listing to Zillow, Realtor.com, Redfin, and dozens of other platforms. This is the moment your home officially goes &quot;active&quot; and the clock starts ticking on your days on market—a metric that every buyer and buyer&apos;s agent watches closely.
      </p>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">
        The Critical First Two Weeks
      </h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        The first fourteen days on the market are the most important period in your entire selling timeline. This is when your listing gets the most visibility, the most clicks, and the most showing requests. Buyers and their agents are constantly monitoring new listings, and a fresh, well‑priced home generates immediate interest. If your home is priced correctly and shows well, you should see multiple showing requests within the first week and potentially receive offers by the end of week two.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        If two weeks pass without a single showing request, something is wrong—almost always the price. If you are getting showings but no offers, the issue is typically the home&apos;s condition or how it presents in person versus the photos. Either way, waiting and hoping is not a strategy. The longer a listing sits, the more &quot;stale&quot; it becomes in buyers&apos; eyes, and the harder it is to generate urgency.
      </p>

      <div className="rounded-2xl overflow-hidden shadow-lg my-12 h-80">
        <img
          src="https://public.readdy.ai/ai/img_res/5e8a27b1903bd5667e46601f692f752e.jpg"
          alt="Real estate agent placing for sale sign in front of El Paso home on listing day"
          title="Active listing stage of the El Paso home selling timeline"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">
        What Affects Days on Market in El Paso
      </h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Several factors determine how long your El Paso home will sit on the market during the active listing phase. Understanding these variables helps you set realistic expectations and make proactive adjustments if your home is not attracting offers.
      </p>

      <div className="bg-gray-50 rounded-2xl p-6 md:p-8 my-10 border border-gray-200">
        <h3 className="text-xl font-bold text-black mb-6">Factors That Influence Days on Market</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="py-3 pr-4 font-bold text-black">Factor</th>
                <th className="py-3 pr-4 font-bold text-black">Faster Sale</th>
                <th className="py-3 font-bold text-black">Slower Sale</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">Price vs. Market Value</td>
                <td className="py-3 pr-4">At or slightly below comps</td>
                <td className="py-3">5%+ above comps</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <td className="py-3 pr-4 font-medium">Condition</td>
                <td className="py-3 pr-4">Move‑in ready, updated</td>
                <td className="py-3">Needs major repairs</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">Season</td>
                <td className="py-3 pr-4">Spring (March–May)</td>
                <td className="py-3">Winter holidays (Nov–Jan)</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <td className="py-3 pr-4 font-medium">Location</td>
                <td className="py-3 pr-4">East/Northeast El Paso</td>
                <td className="py-3">Rural outskirts, flood zones</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">Price Range</td>
                <td className="py-3 pr-4">$180K–$300K (highest demand)</td>
                <td className="py-3">$400K+ (smaller buyer pool)</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <td className="py-3 pr-4 font-medium">Financing Compatibility</td>
                <td className="py-3 pr-4">Passes FHA/VA inspection</td>
                <td className="py-3">Cash‑only due to condition</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">Photography Quality</td>
                <td className="py-3 pr-4">Professional, bright, staged</td>
                <td className="py-3">Phone photos, cluttered rooms</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Seasonality plays a meaningful role in El Paso. The spring market (March through May) is consistently the strongest selling season, with the highest buyer activity and the shortest days on market. Summer slows slightly due to El Paso&apos;s extreme heat—fewer buyers want to tour homes when it is 105 degrees outside. Fall brings a secondary wave of activity, particularly from military families relocating to Fort Bliss. Winter is the slowest season, though El Paso&apos;s mild winters mean the seasonal dip is less dramatic than in northern markets.
      </p>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">
        Managing Showings and Open Houses
      </h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        During the active listing phase, your home needs to be available for showings on short notice—often with just a few hours&apos; warning. This is one of the most disruptive aspects of selling a home, especially if you are still living in the property. You will need to keep the home spotless at all times, leave during showings (buyers are uncomfortable touring a home with the owner present), and accommodate evening and weekend requests.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        For sellers with{' '}
        <Link
          to="/get-cash/sell-house-with-tenants-el-paso"
          className="text-[#D4AF37] hover:underline font-semibold"
        >
          tenants occupying the property
        </Link>
        , the showing process becomes significantly more complicated. Texas law requires reasonable notice before entering a tenant‑occupied property, and uncooperative tenants can effectively sabotage showings by refusing access, leaving the home in poor condition, or making buyers uncomfortable. This is a major reason why landlords with difficult tenants often choose alternative selling methods.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Your agent should provide weekly feedback reports summarizing showing activity, buyer comments, and any patterns that suggest adjustments are needed. If you are averaging fewer than two showings per week after the first two weeks, a price reduction is almost certainly necessary.
      </p>

      <h2
        id="stage-3-offer-and-negotiation-3-to-10-days"
        className="text-4xl font-bold text-black mt-16 mb-6"
      >
        Stage 3: Offer &amp; Negotiation (3 to 10 Days)
      </h2>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        When a buyer submits an offer on your El Paso home, the negotiation phase begins. In Texas, the standard residential contract (TREC 1‑4) is used for most transactions, and it contains specific timelines and deadlines that both parties must follow. Understanding these deadlines is essential because missing one can cost you the deal—or lock you into unfavorable terms.
      </p>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">
        Evaluating and Responding to Offers
      </h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        When an offer arrives, you have three options: accept it as written, reject it outright, or submit a counteroffer. Most El Paso transactions involve at least one round of counteroffers before both parties reach agreement. The key elements to evaluate in every offer include:
      </p>

      <ul className="list-disc pl-6 space-y-3 text-lg text-gray-700 mb-6">
        <li><strong>Offer price:</strong> How does it compare to your asking price and the CMA data? In the current El Paso market, offers typically come in at 95 to 100 percent of asking price for correctly priced homes.</li>
        <li><strong>Financing type:</strong> Cash offers close fastest (7–14 days). Conventional loans take 30–45 days. FHA and VA loans take 30–60 days and come with stricter inspection requirements.</li>
        <li><strong>Option period:</strong> Texas contracts include an &quot;option period&quot; (typically 7–10 days) during which the buyer can terminate for any reason. A shorter option period reduces your risk.</li>
        <li><strong>Earnest money:</strong> Typically one to two percent of the purchase price in El Paso. Higher earnest money signals a more serious buyer.</li>
        <li><strong>Closing date:</strong> Does the proposed closing date align with your timeline? Most El Paso contracts specify a closing date 30 to 45 days from execution.</li>
        <li><strong>Contingencies:</strong> Financing contingency, appraisal contingency, and sale‑of‑buyer&apos;s‑home contingency all add risk and potential delays.</li>
        <li><strong>Seller concessions:</strong> Is the buyer asking you to pay a portion of their closing costs? This reduces your net proceeds.</li>
      </ul>

      <div className="rounded-2xl overflow-hidden shadow-lg my-12 h-80">
        <img
          src="https://readdy.ai/api/search-image?query=real%20estate%20agent%20presenting%20purchase%20offer%20documents%20to%20homeowner%20at%20dining%20table%2C%20professional%20negotiation%20meeting%20with%20contract%20paperwork%20and%20pen%2C%20warm%20natural%20lighting%20in%20modern%20El%20Paso%20Texas%20home%20interior%2C%20serious%20discussion%20about%20home%20sale%20terms%20and%20pricing%2C%20clean%20documentary%20photography%20of%20offer%20review%20process&width=1000&height=600&seq=timeline-ep-offer-004&orientation=landscape"
          alt="Real estate agent presenting purchase offer to El Paso homeowner for review"
          title="Offer and negotiation stage of the El Paso home selling timeline"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">
        Multiple Offers vs. Single Offers
      </h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        In a strong seller&apos;s market, you may receive multiple offers simultaneously, giving you leverage to negotiate better terms. In the current El Paso market—which is shifting toward balance—multiple‑offer situations are becoming less common for most price ranges. If you receive a single reasonable offer, do not assume a better one is coming. Statistically, the first offer on an El Paso home is often the best offer, particularly if the home has been on the market for more than three weeks.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        The negotiation phase typically takes three to ten days from the first offer to a fully executed contract. If negotiations stall or the buyer walks away, you are back to the active listing phase—and your days‑on‑market counter keeps ticking, which can make your listing appear less desirable to future buyers.
      </p>
    </div>
  );
}
